/**
 * Which m² a percentage is calculated on — and when to shout about it.
 *
 * Every "% uitgevoerd" and every vordering percentage is `m² ÷ basis`. The basis is
 * the project's legplan m², and when that was never filled in, the offerte m² —
 * the surface actually sold. It is NEVER the sum of the collectors: that is the
 * same set of collectors a PL ticks when vordering, so ticking them all divides a
 * number by itself and reads 100 %. That fallback existed until 2026-09-14 and put
 * a real client invoice out for the whole contract on a project whose legplan was
 * left empty.
 *
 * MIRRORS the server, which is authoritative for what is actually billed:
 * `resolveM2Basis` in portal-api/src/routes/vorderingen.ts. Keep the two identical —
 * a divergence previews one percentage and bills another.
 */

export type M2BasisSource = 'legplan' | 'offerte' | 'none';

export interface M2Basis {
  /** The denominator. 0 = there is none, so there is no automatic percentage. */
  value: number;
  source: M2BasisSource;
  /** The stored legplan m² — 0 when not filled in. */
  legplanM2: number;
  /** The offerte m² of the sale order — 0 when unknown. */
  offerteM2: number;
  /** |legplan − offerte| ÷ offerte, or null when both numbers aren't there. */
  deviation: number | null;
  /** The legplan is in use AND is more than M2_DEVIATION_LIMIT off the offerte. */
  deviates: boolean;
}

/** A legplan this far off the sold surface is wrong often enough to stop for.
 *  Display-only, so it lives here and not on the server: the portal warns about
 *  the number, it never refuses to bill it. */
export const M2_DEVIATION_LIMIT = 0.1;

export function resolveM2Basis(legplanM2: number, offerteM2: number): M2Basis {
  const legplan = legplanM2 > 0 ? legplanM2 : 0;
  const offerte = offerteM2 > 0 ? offerteM2 : 0;
  const deviation = legplan > 0 && offerte > 0 ? Math.abs(legplan - offerte) / offerte : null;
  const source: M2BasisSource = legplan > 0 ? 'legplan' : offerte > 0 ? 'offerte' : 'none';
  return {
    value: source === 'legplan' ? legplan : source === 'offerte' ? offerte : 0,
    source,
    legplanM2: legplan,
    offerteM2: offerte,
    deviation,
    deviates: source === 'legplan' && deviation !== null && deviation > M2_DEVIATION_LIMIT,
  };
}

/** What a percentage on this basis is a percentage OF, for "42 % van …". */
export function m2BasisLabel(source: M2BasisSource): string {
  return source === 'offerte' ? 'offerte m²' : 'verlegplan';
}

export interface M2Notice {
  /** `danger` = a number is probably wrong; `warn` = usable but not the real basis. */
  tone: 'danger' | 'warn';
  text: string;
}

const fmt = (n: number): string => `${new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 0 }).format(n)} m²`;

/**
 * The one sentence to put next to a percentage. null when the legplan is present
 * and agrees with the offerte — the normal case says nothing.
 */
export function m2BasisNotice(basis: M2Basis): M2Notice | null {
  if (basis.source === 'none') {
    return {
      tone: 'danger',
      text: 'Geen legplan m² en geen offerte m² — er is geen automatisch percentage. Vul het percentage zelf in.',
    };
  }
  if (basis.source === 'offerte') {
    return {
      tone: 'warn',
      text: `Legplan m² is niet ingevuld — het percentage wordt berekend op de offerte (${fmt(basis.offerteM2)}). Vul het legplan in voor een juiste vordering.`,
    };
  }
  if (basis.deviates) {
    const pct = Math.round((basis.deviation ?? 0) * 100);
    const richting = basis.legplanM2 > basis.offerteM2 ? 'meer' : 'minder';
    return {
      tone: 'danger',
      text: `Legplan ${fmt(basis.legplanM2)} is ${pct}% ${richting} dan de offerte (${fmt(basis.offerteM2)}). Controleer het legplan voor je vordert.`,
    };
  }
  return null;
}
